import { useCallback, useEffect, useRef, useState } from "react";
import { PageId, SubscriptionStatus } from "../types";
import { ConsoleKind, accessRedirect } from "../lib/consoleNav";

/**
 * ENG-3526 / ENG-3614 — the one line that says why the console moved someone off a page.
 *
 * An organization page the viewer cannot open is sent to their landing page (lib/consoleNav
 * `accessRedirect`), and the page they land on says why. ENG-3526 kept that in DashboardLayout, which
 * never saw it happen on a real load: App knows the console as soon as /subscription-status answers
 * and redirects then, but mounts the layout only after the rest of sign-in has run, so by the time the
 * layout existed the refused page was gone. A switch is a full reload, so the case ENG-3526 was for
 * (on Policy in one client, switch to another where the access is Read only) never showed the line.
 *
 * So it is held where the redirect happens: App calls this, and hands the result to the layout.
 *
 * [asked] is the page refused and [landed] the page it was sent to. The notice is kept for exactly the
 * page it landed on; the next navigation drops it.
 *
 * ENG-3652: the notice also belongs to exactly one signed-in account. App never remounts on a sign-out
 * or an account switch in the same tab (only DashboardLayout does, via `key={auth.currentUser?.uid}`),
 * so without [uid] a notice held for the "activePage unchanged" case above (e.g. a family admin denied
 * Policy and landed on Home) survived into the next account that also landed on Home — nothing about
 * that transition changes [activePage] or the denial, so the effect below would never have re-run.
 * [uid] is read from `auth.currentUser?.uid` and is the one thing guaranteed to change on every
 * sign-out and every account switch; a change drops the previous account's held notice rather than
 * re-holding it. If the newly signed-in account is itself denied a page, that is a fresh notice of its
 * own, and the denial branch below shows it.
 */
export interface AccessNotice {
  asked: PageId;
  landed: PageId;
}

export function useAccessNotice(
  activePage: PageId,
  status: SubscriptionStatus | null | undefined,
  kind: ConsoleKind,
  uid: string | null,
): [AccessNotice | null, () => void] {
  const deniedRedirect = accessRedirect(activePage, status, kind);
  const [notice, setNotice] = useState<AccessNotice | null>(null);
  const lastUid = useRef(uid);
  useEffect(() => {
    const uidChanged = lastUid.current !== uid;
    lastUid.current = uid;
    if (deniedRedirect) {
      setNotice({ asked: activePage, landed: deniedRedirect });
      return;
    }
    if (uidChanged) {
      setNotice(null);
      return;
    }
    setNotice((held) => (held && held.landed === activePage ? held : null));
  }, [activePage, deniedRedirect, uid]);
  const dismiss = useCallback(() => setNotice(null), []);
  return [notice, dismiss];
}
